import { useMemo, useState } from "react";
import { ghostBtnClass, inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  OVERHEAD_BUCKET_PAGE_SIZE,
  UNCATEGORIZED_BUCKET,
  filterOverheadBucketTree,
  overheadBucketCategoryOptions,
  overheadNestParentId,
  pageOverheadBucketRoots,
} from "@/lib/manufacturing/overheadBucketTaxonomy";
import type { MfgOverheadBucketTreeNode } from "@/lib/manufacturing/overheadTypes";
import { OverheadBucketEditForm } from "./OverheadBucketEditForm";
import { OverheadBucketTree } from "./OverheadBucketTree";
import { EMPTY_OVERHEAD_BUCKET_DRAFT, type ManufacturingOverheadBucketsState } from "./useManufacturingOverheadBuckets";

export function OverheadBucketTaxonomy({
  buckets,
  plantId,
  upcomingConsumptionOn,
  consumptionConfigured,
}: {
  buckets: ManufacturingOverheadBucketsState;
  plantId: string;
  upcomingConsumptionOn: string | null;
  consumptionConfigured: boolean;
}) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [collapsedIds, setCollapsedIds] = useState<Record<string, boolean>>({});
  const categories = useMemo(
    () => overheadBucketCategoryOptions(buckets.flatBuckets),
    [buckets.flatBuckets],
  );
  const hasUncategorized = buckets.flatBuckets.some((bucket) => !bucket.category.trim());
  const filtered = useMemo(
    () => filterOverheadBucketTree(buckets.tree, query, category, buckets.editingId),
    [buckets.editingId, buckets.tree, category, query],
  );
  const page = pageOverheadBucketRoots(filtered, pageIndex, OVERHEAD_BUCKET_PAGE_SIZE);

  function dropNest(sourceId: string, target: MfgOverheadBucketTreeNode | null) {
    const source = buckets.flatBuckets.find((bucket) => bucket.id === sourceId);
    if (!source) return;
    const decision = overheadNestParentId(
      {
        id: source.id,
        parent_bucket_id: source.parent_bucket_id,
        is_aggregate: source.is_aggregate,
        has_children: buckets.flatBuckets.some((bucket) => bucket.parent_bucket_id === source.id),
      },
      target
        ? {
            id: target.id,
            parent_bucket_id: target.parent_bucket_id,
            is_aggregate: target.is_aggregate,
            has_children: target.children.length > 0,
          }
        : null,
    );
    if (!decision) return;
    if ("error" in decision) {
      buckets.setLocalError(decision.error);
      return;
    }
    void buckets.moveBucket(source.id, decision.parent_bucket_id);
  }

  return (
    <div className="space-y-3">
      {buckets.showCreate ? (
        <OverheadBucketEditForm
          title="Create bucket"
          draft={buckets.createDraft}
          setDraft={buckets.setCreateDraft}
          bucketTypes={buckets.bucketTypes}
          parentOptions={buckets.flatBuckets.filter((bucket) => !bucket.parent_bucket_id)}
          busy={buckets.actionBusy}
          onCancel={() => {
            buckets.setShowCreate(false);
            buckets.setCreateDraft(EMPTY_OVERHEAD_BUCKET_DRAFT);
          }}
          onSubmit={() => void buckets.submitCreate()}
        />
      ) : null}
      <div className="flex flex-col gap-2">
        <input
          className={inputClass}
          value={query}
          placeholder="Search buckets"
          aria-label="Search buckets"
          onChange={(event) => {
            setQuery(event.target.value);
            setPageIndex(0);
          }}
        />
        <select
          className={inputClass}
          value={category}
          aria-label="Category"
          onChange={(event) => {
            setCategory(event.target.value);
            setPageIndex(0);
          }}
        >
          <option value="">All categories</option>
          {hasUncategorized ? <option value={UNCATEGORIZED_BUCKET}>Uncategorized</option> : null}
          {categories.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </div>
      <p className="text-xs text-gray-500 dark:text-zinc-400">Drag a bucket onto a top-level bucket to nest it.</p>
      {page.total === 0 ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">No buckets match.</div>
      ) : (
        <OverheadBucketTree
          nodes={page.page}
          state={buckets}
          plantId={plantId}
          upcomingConsumptionOn={upcomingConsumptionOn}
          consumptionConfigured={consumptionConfigured}
          nesting={{
            draggingId,
            hoverId,
            onDragStart: setDraggingId,
            onDragEnd: () => {
              setDraggingId(null);
              setHoverId(null);
            },
            onHover: setHoverId,
            onDrop: dropNest,
          }}
          collapsedIds={collapsedIds}
          onToggleCollapsed={(id) => setCollapsedIds((current) => ({ ...current, [id]: !current[id] }))}
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600 dark:text-zinc-400">
        <span>
          {page.total === 0 ? "0 buckets" : `${page.rangeStart}–${page.rangeEnd} of ${page.total}`}
        </span>
        {page.pageCount > 1 ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={ghostBtnClass}
              disabled={page.pageIndex <= 0}
              onClick={() => setPageIndex(page.pageIndex - 1)}
            >
              Previous
            </button>
            <span>
              {page.pageIndex + 1} / {page.pageCount}
            </span>
            <button
              type="button"
              className={ghostBtnClass}
              disabled={page.pageIndex + 1 >= page.pageCount}
              onClick={() => setPageIndex(page.pageIndex + 1)}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
